/* iter55g69 — JSON-LD Structured Data component.
 *
 * Emits a schema.org JSON-LD `<script>` in the document head so Google
 * / Bing / DuckDuckGo can render rich results (organisation cards,
 * sitelinks search box, product cards, author profile cards).
 *
 * Usage:
 *   <StructuredData data={{ '@context': 'https://schema.org', ... }} />
 *
 * Auto-strips on unmount so admin panels / signed-in-app pages don't
 * inherit marketing structured data.
 *
 * Why this matters for a young domain: for a site younger than ~6
 * months without established backlink authority, structured data is
 * the single biggest lever for rich SERP presentation. It doesn't
 * boost rank directly but it hugely improves CTR, which feeds back
 * into rank. Organization + WebSite (with SearchAction) on the home
 * page unlocks the "sitelinks search box" and a knowledge-panel
 * candidacy.
 */
import { useEffect } from 'react';

export default function StructuredData({ id, data }) {
  useEffect(() => {
    if (!data) return undefined;
    const scriptId = `ld-json-${id || 'default'}`;
    let el = document.getElementById(scriptId);
    const fresh = !el;
    if (fresh) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    try {
      el.textContent = JSON.stringify(data);
    } catch (err) {
      // Circular reference or non-serialisable — log and skip.
      // Never throw here; SEO is decorative.
      // eslint-disable-next-line no-console
      console.warn('[StructuredData] non-serialisable payload:', err);
      return undefined;
    }
    return () => {
      if (fresh) {
        try { el.remove(); } catch { /* noop */ }
      }
    };
  }, [id, data]);
  return null;
}


// ---- Preset builders ------------------------------------------------------
// Small helpers so pages don't have to memorise the full schema.org
// vocabulary. Each returns a plain object suitable for
// `<StructuredData data={...} />`.

/** Organization card — put on the landing page.
 *  Unlocks the right-side knowledge panel + logo-in-SERP candidacy.
 *
 *  iter-p2 — Accepts `socialProfiles` (array of full URLs) which
 *  become schema.org `sameAs` entries; these tell Google which
 *  Twitter/X, Instagram, LinkedIn, YouTube, etc. profiles belong to
 *  this organization and strengthen the knowledge-panel candidacy.
 *
 *  2026-02 · `lang` param — informs AI engines (GEO) of the page
 *  language. Pass the current i18n locale code.
 */
export function organizationLd({ siteUrl, logoUrl, socialProfiles = [], lang = 'en' }) {
  return {
    '@context': 'https://schema.org',
    '@type':    'Organization',
    '@id':      `${siteUrl}/#organization`,
    name:       'Crown & Rose, LLC',
    legalName:  'Crown & Rose, LLC',
    url:        siteUrl,
    logo:       logoUrl,
    description: 'AI novel-writing studio and indie-fiction bookstore for serious novelists.',
    inLanguage: lang,
    availableLanguage: ['en', 'fr', 'de', 'es', 'pt', 'zh'],
    sameAs:     Array.isArray(socialProfiles)
      ? socialProfiles.filter((u) => typeof u === 'string' && /^https?:\/\//.test(u))
      : [],
    contactPoint: {
      '@type':          'ContactPoint',
      contactType:      'customer support',
      email:            'info@crownnrose.com',
      availableLanguage: ['en', 'fr', 'de', 'es', 'pt', 'zh'],
    },
  };
}

/** WebSite entity — pair with Organization on the landing.
 *  Unlocks the "sitelinks search box" in the SERP result. */
export function websiteLd({ siteUrl, lang = 'en' }) {
  return {
    '@context': 'https://schema.org',
    '@type':    'WebSite',
    '@id':      `${siteUrl}/#website`,
    name:       'Crown & Rose',
    url:        siteUrl,
    inLanguage: lang,
    availableLanguage: ['en', 'fr', 'de', 'es', 'pt', 'zh'],
    potentialAction: {
      '@type':      'SearchAction',
      target:       `${siteUrl}/bookstore?q={search_term_string}`,
      // schema.org spec requires this weird placeholder — keep as-is.
      'query-input': 'required name=search_term_string',
    },
  };
}

/** SoftwareApplication — describes The Loom to Google.
 *  Used on the landing page alongside Organization/WebSite.
 *
 *  Pricing reflects the 3-tier dual-mode (Standard + BYOK) range
 *  consolidated in Feb-2026: Author / Publisher / Studio.
 *  Token top-up packs available from $19.99 (all tiers).
 */
export function softwareApplicationLd({ siteUrl, lang = 'en' }) {
  // Canonical plan list — mirrors DEFAULT_PLANS in subscriptions_service.py.
  // Keeping this in sync with the backend avoids stale price assertions in
  // structured data (Google's policies penalise inaccurate price mark-up).
  // Updated: Feb-2026 pricing revision — Scribe $14 / Author $39 / Publisher $119 / Studio $449.
  const PLANS = [
    { name: 'Scribe (BYOK required)',   price: '14.00',  byok: true  },
    { name: 'Author (Standard)',        price: '39.00',  byok: false },
    { name: 'Author (BYOK)',            price: '25.00',  byok: true  },
    { name: 'Publisher (Standard)',     price: '119.00', byok: false },
    { name: 'Publisher (BYOK)',         price: '79.00',  byok: true  },
    { name: 'Studio (Standard)',        price: '449.00', byok: false },
    { name: 'Studio BYOK',             price: '299.00', byok: true  },
  ];

  return {
    '@context':       'https://schema.org',
    '@type':          'SoftwareApplication',
    name:             'The Loom by Crown & Rose',
    operatingSystem:  'Web',
    applicationCategory: 'WritingApplication',
    description: 'The only framework-driven AI novel-production studio — authors follow guided workflows (Plot Blueprint, KDP Publishing Framework, Edit Walker, Continuity Audit) without needing to understand AI. One subscription covers everything: multi-model AI co-author (Claude, GPT, Gemini), Manuscript Polish, Arc Visualizer, Voice Coach, audiobook narration (pay per production), marketing tools, and a direct-to-reader bookstore. Four tiers: Scribe $14/mo (BYOK only), Author $39/mo (1.5M tokens), Publisher $119/mo (4.5M tokens), Studio $449/mo (20M tokens). BYOK from $14/mo. Token top-up packs from $10.',
    url:              siteUrl,
    inLanguage:       lang,
    availableLanguage: ['en', 'fr', 'de', 'es', 'pt', 'zh'],
    keywords: 'AI novel writing software, scrivener alternative, sudowrite alternative, novelcrafter alternative, AI writing tool, long-form fiction AI, bulk novel generation, continuity audit, arc visualizer, character bible',
    featureList: [
      'Multi-model AI drafting: Claude, GPT-4o, and Gemini per project',
      'Bulk novel generation: full manuscript in one automated job (Publisher+)',
      'Character Bible with archetype extraction and arc visualization',
      'Continuity Audit with drift correction and history trend chart',
      'Edit Walker: up to 2 full developmental edit passes/mo (Author); unlimited Publisher+',
      'Full Novel Continuity Audit — deep manuscript review (Publisher+)',
      'Framework-Driven Workflows: Plot Blueprint, Character Bible, KDP Publishing Framework, Edit Walker, Continuity Audit — authors follow guided forms, never craft AI prompts. Zero AI expertise required.',
      'KDP Publishing Framework: BISAC selection, 7-keyword optimisation, content-policy checks, front/back matter templates, publish checklist. Step-by-step guidance with no manual research.',
      'Manuscript Readiness Check: weekly completeness digest emails authors about missing synopsis, cover, keywords, word count with direct fix links.',
      'Audiobook narration with AI voice cloning — pay per production (Publisher+)',
      'Voice Coach: series-level voice memory across all chapters',
      'Marketing suite: Amazon blurb, ARC pitch, tweet thread, social calendar, reviewer CRM',
      'Direct-to-reader bookstore with 85% author royalties',
      'OHAS D3 human-authorship certification for AI-assisted manuscripts',
      'One-click DOCX and KDP-trim PDF export',
      'BYOK mode: Author $25/mo, Publisher $79/mo, Studio $299/mo',
      'Token top-up packs: Starter $10 (50K tokens), Pro $40 (250K tokens), Power $120 (1M tokens). Monthly included: Author 1.5M, Publisher 4.5M, Studio 20M.',
    ],
    // AggregateOffer captures the full price range across all 10 plan variants.
    offers: {
      '@type':        'AggregateOffer',
      priceCurrency:  'USD',
      lowPrice:       '25.00',  // Author BYOK/mo
      highPrice:      '449.00', // Studio Standard/mo
      offerCount:     String(PLANS.length),
      offers: PLANS.map((p) => ({
        '@type':        'Offer',
        name:           p.name,
        price:          p.price,
        priceCurrency:  'USD',
        priceSpecification: {
          '@type':           'UnitPriceSpecification',
          price:             p.price,
          priceCurrency:     'USD',
          unitCode:          'MON',  // per month
          billingDuration:   1,
          billingIncrement:  1,
        },
        description: p.byok
          ? `${p.name}: Bring-Your-Own-Key plan — platform fee only; LLM usage bills your own API key.`
          : `${p.name}: Full-service plan with managed token allowance; no external key required.`,
        url: `${siteUrl}/#pricing`,
        availability: 'https://schema.org/InStock',
      })),
    },
  };
}

/** Bookstore CollectionPage — for /bookstore. */
export function bookstoreCollectionLd({ siteUrl, lang = 'en' }) {
  return {
    '@context': 'https://schema.org',
    '@type':    'CollectionPage',
    name:       'Indie Fiction Ebook Store — Crown & Rose',
    url:        `${siteUrl}/bookstore`,
    inLanguage: lang,
    description: 'Discover indie novels by writers who actually finished. 85% of every sale goes direct to the author.',
  };
}

/** Person + ProfilePage for an author's public profile page. */
export function authorProfileLd({ siteUrl, penName, penSlug, bio, portraitUrl, socialUrls = [] }) {
  return {
    '@context': 'https://schema.org',
    '@type':    'ProfilePage',
    mainEntity: {
      '@type':      'Person',
      name:         penName,
      description:  bio,
      image:        portraitUrl || undefined,
      url:          `${siteUrl}/authors/${penSlug}`,
      sameAs:       socialUrls,
    },
  };
}

/** Book — for /book/:isbn print-QR landing pages. Unlocks price/author
 *  rich results. `offers` is only included when a real retail price
 *  exists so we never fabricate pricing data for a book. */
export function bookLd({ siteUrl, isbn, title, authors = [], description, coverUrl, language, format, retailPrice, currency }) {
  const data = {
    '@context': 'https://schema.org',
    '@type':    'Book',
    name:       title,
    isbn,
    description,
    image:      coverUrl || undefined,
    inLanguage: language || undefined,
    bookFormat: format === 'ebook' ? 'https://schema.org/EBook' : undefined,
    url:        `${siteUrl}/book/${isbn}`,
    author:     (authors || []).map((name) => ({ '@type': 'Person', name })),
  };
  if (retailPrice) {
    data.offers = {
      '@type':         'Offer',
      price:           String(retailPrice),
      priceCurrency:   currency || 'USD',
      availability:    'https://schema.org/InStock',
      url:             `${siteUrl}/book/${isbn}`,
    };
  }
  return data;
}

/** BreadcrumbList — pass an ordered array of {name, url}. Unlocks the
 *  breadcrumb trail under the SERP title (Bookstore > Genre > Book). */
export function breadcrumbLd(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type':   'ListItem',
      position:  i + 1,
      name:      item.name,
      item:      item.url,
    })),
  };
}

/**
 * VideoObject — for the Mei welcome video on locale landing pages.
 *
 * The `transcript` field is read by AI citation engines (Perplexity,
 * ChatGPT, Gemini Deep Research) to understand what the video says and
 * attribute it to Crown & Rose in generative answers. Each locale
 * landing page should inject this with its own-language transcript.
 *
 * 2026-02 · Added for GEO (Generative Engine Optimisation).
 */
export function videoObjectLd({
  name,
  description,
  transcript,
  contentUrl = '',
  thumbnailUrl = '',
  uploadDate = '2025-10-01',
  lang = 'en',
}) {
  return {
    '@context':    'https://schema.org',
    '@type':       'VideoObject',
    name:          name || 'Mei introduces Crown & Rose',
    description:   description || transcript?.slice(0, 160) || '',
    inLanguage:    lang,
    transcript,
    uploadDate,
    ...(contentUrl   ? { contentUrl }   : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    author: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
      url:     'https://crownnrose.com',
    },
    publisher: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
    },
    potentialAction: {
      '@type':  'WatchAction',
      target:   contentUrl || 'https://crownnrose.com',
    },
  };
}

/**
 * WebPage — for informational marketing pages (Trust, DEI, WhatWeOffer).
 * Gives crawlers and LLMs a canonical description they can cite directly.
 *
 * @param {object} p
 * @param {string} p.siteUrl      — e.g. window.location.origin
 * @param {string} p.path         — e.g. '/trust'
 * @param {string} p.name         — page title without site suffix
 * @param {string} p.description  — brief description (≤160 chars)
 * @param {string} [p.lang]       — BCP47, defaults to 'en'
 */
export function webPageLd({ siteUrl, path = '', name, description, lang = 'en' }) {
  return {
    '@context':  'https://schema.org',
    '@type':     'WebPage',
    '@id':       `${siteUrl}${path}#webpage`,
    name,
    description,
    url:         `${siteUrl}${path}`,
    inLanguage:  lang,
    isPartOf:    { '@id': `${siteUrl}/#website` },
    publisher: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
      url:     siteUrl,
    },
  };
}

/**
 * Blog index — emits a `Blog` entity so Google treats the page as a
 * content collection root rather than a plain list.
 *
 * @param {object} p
 * @param {string} p.siteUrl  — e.g. window.location.origin
 * @param {string} [p.lang]   — BCP47, defaults to 'en'
 */
export function blogIndexLd({ siteUrl, lang = 'en' }) {
  return {
    '@context':  'https://schema.org',
    '@type':     'Blog',
    '@id':       `${siteUrl}/blog#blog`,
    name:        'The Loom Blog — Crown & Rose',
    description: 'Practical writing on AI-assisted novel writing, mixed-LLM continuity, developmental editing, and indie publishing for independent authors.',
    url:         `${siteUrl}/blog`,
    inLanguage:  lang,
    isPartOf:    { '@id': `${siteUrl}/#website` },
    author: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
      url:     siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
    },
  };
}

/**
 * CollectionPage — for the community templates marketplace.
 *
 * @param {object} p
 * @param {string} p.siteUrl  — e.g. window.location.origin
 * @param {string} [p.lang]   — BCP47, defaults to 'en'
 */
export function collectionPageLd({ siteUrl, lang = 'en' }) {
  return {
    '@context':  'https://schema.org',
    '@type':     'CollectionPage',
    '@id':       `${siteUrl}/marketplace#collection`,
    name:        'Community Templates Marketplace — Crown & Rose',
    description: 'Browse author-crafted novel stencils — character bibles, beat sheets, chapter outlines — built by working indie authors. 85% of every sale goes to the author.',
    url:         `${siteUrl}/marketplace`,
    inLanguage:  lang,
    isPartOf:    { '@id': `${siteUrl}/#website` },
    publisher: {
      '@type': 'Organization',
      name:    'Crown & Rose, LLC',
      url:     siteUrl,
    },
  };
}
